import clsx from 'clsx';
import { Coin2 } from '@hungryroot/ui-kit';
import {
  Paragraph,
  Heading,
  type HeadingVariant,
  type ParagraphVariant,
} from '@hungryroot/ui-kit';

import styles from './PointsWithIcon.module.css';

export enum PointsWithIconVariant {
  Default = 'default',
  Large = 'large',
  Removed = 'removed',
  Small = 'small',
}

interface Props {
  testid?: string;
  points: string;
  ariaLabel?: string;
  variant?: PointsWithIconVariant;
  className?: string;
  containerClassName?: string;
  pointsAfterDiscount?: number | null;
  /**
   * What the item costs once Reward Credits are applied. `0` renders only the
   * struck-through original; a positive value renders it before the original.
   */
  pointsAfterRewards?: number | null;
  discountedPriceFirst?: boolean;
  stackedDisplay?: boolean;
  salePriceClassName?: string;
  originalPriceClassName?: string;
}

export function PointsWithIcon(props: Props) {
  const {
    ariaLabel,
    testid,
    points,
    variant = PointsWithIconVariant.Default,
    className,
    containerClassName,
    pointsAfterDiscount,
    pointsAfterRewards,
    discountedPriceFirst = true,
    stackedDisplay = false,
    salePriceClassName,
    originalPriceClassName,
  } = props;

  const paragraphVariantMap: Record<PointsWithIconVariant, ParagraphVariant> = {
    [PointsWithIconVariant.Default]: 'body',
    [PointsWithIconVariant.Small]: 'caption-xs',
    [PointsWithIconVariant.Large]: 'caption',
    [PointsWithIconVariant.Removed]: 'caption',
  };
  const paragraphVariant = paragraphVariantMap[variant] ?? 'caption';
  const headerVariantMap: Record<PointsWithIconVariant, HeadingVariant> = {
    [PointsWithIconVariant.Default]: 'H6',
    [PointsWithIconVariant.Small]: 'H6',
    [PointsWithIconVariant.Large]: 'H3',
    [PointsWithIconVariant.Removed]: 'H6',
  };
  const headerVariant = headerVariantMap[variant] ?? 'H6';

  const pointsContent = (
    <>
      <Coin2 />
      <Heading
        testid={`${testid}`}
        className={className}
        aria-label={ariaLabel}
        variant={headerVariant}
      >
        {points === 'FREE' ? 'FREE' : parseFloat(points)}
      </Heading>
    </>
  );

  const discountedPriceElement = (
    <span className={clsx(styles.container, styles['red'], salePriceClassName)}>
      <Coin2 />
      <Heading
        variant={headerVariant}
        testid={`${testid}-discounted`}
        className={className}
        aria-label={`Discounted to ${pointsAfterDiscount} points`}
      >
        {parseFloat(pointsAfterDiscount?.toString() || '')}
      </Heading>
    </span>
  );

  const originalPriceElement = (
    <span
      className={clsx(
        styles['original-price-container'],
        originalPriceClassName
      )}
    >
      <Coin2 />
      <Paragraph
        variant={paragraphVariant}
        testid={`${testid}-original`}
        aria-label={`Original price ${points} points`}
      >
        {parseFloat(points)}
      </Paragraph>
    </span>
  );

  // Design calls for discounted and original price to be shown in different orders in various places
  const discountedContent = pointsAfterDiscount && (
    <>
      {discountedPriceFirst ? (
        <>
          {discountedPriceElement}
          {originalPriceElement}
        </>
      ) : (
        <>
          {originalPriceElement}
          {discountedPriceElement}
        </>
      )}
    </>
  );

  // Boolean for ensuring we fall back to pointsContent if discountedContent can't be rendered
  const showDiscountedContent = pointsAfterDiscount && discountedContent;

  if (variant === PointsWithIconVariant.Removed) {
    return (
      <span
        aria-label={ariaLabel}
        className={clsx(containerClassName, styles['original-price-container'])}
        data-testid={`${testid}-removed`}
      >
        {pointsContent}
      </span>
    );
  }

  if (pointsAfterRewards !== undefined && pointsAfterRewards !== null) {
    return (
      <span
        aria-label={ariaLabel}
        className={styles.container}
        data-testid={`${testid}-rewards`}
      >
        {pointsAfterRewards > 0 && (
          <span className={styles.container}>
            <Coin2 />
            <Heading
              variant={headerVariant}
              testid={`${testid}-after-rewards`}
              className={className}
              aria-label={`${pointsAfterRewards} points after Reward Credits`}
            >
              {pointsAfterRewards}
            </Heading>
          </span>
        )}
        {originalPriceElement}
      </span>
    );
  }

  if (variant === PointsWithIconVariant.Large) {
    return (
      <span
        aria-label={ariaLabel}
        className={clsx(
          styles.large,
          containerClassName,
          showDiscountedContent && stackedDisplay && styles['stacked-display']
        )}
        data-testid={`${testid}-large`}
      >
        {showDiscountedContent ? discountedContent : pointsContent}
      </span>
    );
  }

  return (
    <span
      aria-label={ariaLabel}
      className={clsx(
        styles.container,
        showDiscountedContent && stackedDisplay && styles['stacked-display']
      )}
    >
      {showDiscountedContent ? discountedContent : pointsContent}
    </span>
  );
}
